import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useQuery } from '@apollo/client';
import { Navigate, Route, Routes } from 'react-router-dom';
import 'yet-another-react-lightbox/dist/styles.css';

import { Home } from './pages/Home';
import { AUTH_QUERY } from './gql';
import { SettingsProvider } from './components/context/SettingsContext';
import { ColorModeProvider } from './components/context/ColorModeContext';
import { useColorMode } from './components/context/ColorModeContext/useColorMode';
import { LayerProvider } from './components/LayerContext';
import { createAppTheme, resetStyles } from './theme';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { ResetPassword } from './pages/ResetPassword';
import { VerifyEmail } from './pages/VerifyEmail';
import { FullScreenLoader, GoToProvider } from './components';
import { Account } from './pages/Account';
import { Activity } from './pages/Activity';
import { Changelog } from './pages/Changelog';
import { Sites } from './pages/Sites';
import { Site } from './pages/Site';
import { Objects } from './pages/Objects';
import { Find } from './pages/Find';
import { Finds } from './pages/Finds';
import { Contacts } from './pages/Contacts';
import { WorkspaceProvider } from './components/context/WorkspaceContext';
import { Imports } from './pages/Imports';
import { Admin } from './pages/Admin';
import { Visualizer } from './pages/Visualizer';
import { FieldSessions } from './pages/FieldSessions';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { csCZ, enUS } from '@mui/x-date-pickers/locales';
import { useLocale } from './i18n';

const globalStyles = <GlobalStyles styles={resetStyles} />;

const AppLayout = () => {
  const { loading, data } = useQuery(AUTH_QUERY);
  const user = data ? data.viewer.user : null;
  const isAdmin = user?.role === 'admin';
  const canUsePremiumFeatures =
    isAdmin || data?.viewer?.premiumAccess?.isPremium === true;

  if (loading) {
    return <FullScreenLoader />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home isSignedIn={user != null} />} />{' '}
      <Route
        path="/m/:shareSlug"
        element={<Home isSignedIn={user != null} />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      {!user ? (
        <>
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signup" element={<SignUp />} />
        </>
      ) : (
        <>
          <Route path="/account/*" element={<Account />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/contacts/*" element={<Contacts />} />
          <Route path="/s/:siteId/f/:findId/edit/*" element={<Find />} />
          <Route path="/s/:siteId/f/create" element={<Find />} />
          <Route path="/f/create" element={<Find />} />
          <Route path="/f/:findId/edit/*" element={<Find />} />
          <Route path="/finds/*" element={<Finds />} />
          <Route path="/field-sessions/*" element={<FieldSessions />} />
          <Route path="/s" element={<Sites />} />
          <Route path="/s/create" element={<Site />} />
          <Route path="/s/:siteId/*" element={<Site />} />
          <Route path="/o/*" element={<Objects />} />
          {isAdmin && <Route path="/admin/*" element={<Admin />} />}
          {canUsePremiumFeatures && (
            <>
              <Route path="/imports/*" element={<Imports />} />
              <Route path="/visualizer" element={<Visualizer />} />
            </>
          )}
          <Route path="/signin" element={<Navigate to="/" />} />
          <Route path="/signup" element={<Navigate to="/" />} />
          <Route path="*" element={<div>404</div>} />
        </>
      )}
    </Routes>
  );
};

const AppTheme = ({ children }: React.PropsWithChildren<{}>) => {
  const { mode } = useColorMode();
  const { locale } = useLocale();
  const theme = React.useMemo(() => createAppTheme(mode, locale), [
    locale,
    mode,
  ]);

  return (
    <ThemeProvider theme={theme}>
      {globalStyles}
      {children}
    </ThemeProvider>
  );
};

const AppLocalization = ({ children }: React.PropsWithChildren<{}>) => {
  const { locale } = useLocale();
  const localeText =
    locale === 'cs'
      ? csCZ.components.MuiLocalizationProvider.defaultProps.localeText
      : enUS.components.MuiLocalizationProvider.defaultProps.localeText;

  return (
    <LocalizationProvider
      adapterLocale={locale}
      dateAdapter={AdapterLuxon}
      localeText={localeText}
    >
      {children}
    </LocalizationProvider>
  );
};

export const App = () => (
  <ColorModeProvider>
    <AppLocalization>
      <AppTheme>
        <LayerProvider>
          <GoToProvider>
            <SettingsProvider>
              <WorkspaceProvider>
                <AppLayout />
              </WorkspaceProvider>
            </SettingsProvider>
          </GoToProvider>
        </LayerProvider>
      </AppTheme>
    </AppLocalization>
  </ColorModeProvider>
);
